import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plus, Building2, ChevronRight, Briefcase, FileText, Loader2,
  Clock, CheckCircle, ArrowRight, RefreshCcw, Eye, Send
} from 'lucide-react';
import { casesAPI } from '@/lib/api';
import { toast } from 'sonner';

const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'financials_in_preparation', label: 'Financials in Prep', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'sent_to_cs', label: 'Sent to CS', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  { key: 'filing_in_progress', label: 'Filing in Progress', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
];

const STATUS_MAP = Object.fromEntries(STATUSES.filter(s => s.key !== 'all').map(s => [s.key, s]));

export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    casesAPI.getAll().then(res => setCases(res.data)).catch(() => toast.error('Failed to load cases')).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? cases : cases.filter(c => c.status === filter);
  const counts = cases.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-cases">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">R-PACE Cases</h1>
          <p className="text-slate-600 text-sm">Case execution and assignment management</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/cases/new">
            <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] gap-1.5" data-testid="create-case-btn">
              <Plus className="w-4 h-4" /> New Case
            </Button>
          </Link>
          <span className="text-sm font-semibold text-slate-500">{cases.length} total</span>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="case-status-filter">
        {STATUSES.map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${filter === s.key
              ? (s.key === 'all' ? 'bg-slate-900 text-white border-slate-900' : s.color + ' ring-2 ring-offset-1 ring-slate-300')
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
            }`}
          >
            {s.label} {s.key === 'all' ? `(${cases.length})` : counts[s.key] ? `(${counts[s.key]})` : ''}
          </button>
        ))}
      </div>

      {/* Cases List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{filter === 'all' ? 'No cases yet' : `No cases in "${STATUS_MAP[filter]?.label}" status`}</p>
            {filter === 'all' && <p className="text-slate-400 text-sm mt-1">Create a case or convert a CCFS lead to get started.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => {
            const sMeta = STATUS_MAP[c.status] || { label: c.status, color: 'bg-slate-100 text-slate-600 border-slate-200' };
            return (
              <Card
                key={c.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/cases/${c.id}`)}
                data-testid={`case-row-${c.id}`}
              >
                <CardContent className="p-4 md:p-5">
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    {/* Left: Company info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{c.company_name || 'Unnamed Company'}</p>
                        <p className="text-sm text-slate-500 truncate">{c.client_name}</p>
                      </div>
                    </div>

                    {/* Middle: Assignments */}
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {c.ca_partner ? (
                        <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
                          <Briefcase className="w-3 h-3" /> CA: {c.ca_partner.name}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-slate-50 text-slate-400 px-2 py-1 rounded-full border border-dashed border-slate-300">
                          <Briefcase className="w-3 h-3" /> CA: Unassigned
                        </span>
                      )}
                      {c.cs_partner ? (
                        <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
                          <FileText className="w-3 h-3" /> CS: {c.cs_partner.name}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-slate-50 text-slate-400 px-2 py-1 rounded-full border border-dashed border-slate-300">
                          <FileText className="w-3 h-3" /> CS: Unassigned
                        </span>
                      )}
                    </div>

                    {/* Right: Status + Pool badge (if any side unclaimed) + date */}
                    <div className="flex items-center gap-3">
                      {(!c.ca_partner || !c.cs_partner) && (
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-violet-100 text-violet-700 border border-violet-200" title={!c.ca_partner && !c.cs_partner ? 'Both sides open in pool' : !c.ca_partner ? 'CA slot open in pool' : 'CS slot open in pool'} data-testid={`pool-badge-${c.id}`}>
                          {!c.ca_partner && !c.cs_partner ? 'POOL · CA + CS' : !c.ca_partner ? 'POOL · CA' : 'POOL · CS'}
                        </span>
                      )}
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${sMeta.color}`}>{sMeta.label}</span>
                      <span className="text-xs text-slate-400">{new Date(c.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
